import { useState } from "react";
import Button from "Components/Common/Button/Button";
import Input from "Components/Common/Input/Input";
import CustomModal from "Components/Common/CustomModal/CustomModal";

interface Props {
  initialSmsEnabled?: boolean;
  initialEmailEnabled?: boolean;
  sms2faEnabled?: boolean;
  phone?: string;
  phoneVerified?: boolean;
  onUpdateChannels: (updates: {
    emailEnabled?: boolean;
    smsEnabled?: boolean;
  }) => Promise<boolean>;
  savingNotifications?: boolean;
}

export default function NotificationPreferencesSection({
  initialSmsEnabled = false,
  initialEmailEnabled = true,
  sms2faEnabled = false,
  phone,
  phoneVerified = false,
  onUpdateChannels,
  savingNotifications = false,
}: Props) {
  const [globalEmail, setGlobalEmail] = useState(initialEmailEnabled);
  const [globalSms, setGlobalSms] = useState(initialSmsEnabled);
  const [showSmsConsent, setShowSmsConsent] = useState(false);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);
  const [showSmsBlockMsg, setShowSmsBlockMsg] = useState(false);

  async function toggleGlobalEmail(enabled: boolean) {
    setGlobalEmail(enabled);
    const ok = await onUpdateChannels({ emailEnabled: enabled });
    if (!ok) setGlobalEmail((prev) => !prev);
  }

  function toggleGlobalSms(enabled: boolean) {
    if (enabled) {
      setSmsConsentChecked(false);
      setShowSmsBlockMsg(false);
      setShowSmsConsent(true);
    } else {
      if (sms2faEnabled) {
        setShowSmsBlockMsg(true);
        return;
      }
      setShowSmsBlockMsg(false);
      void (async () => {
        setGlobalSms(false);
        const ok = await onUpdateChannels({ smsEnabled: false });
        if (!ok) setGlobalSms(true);
      })();
    }
  }

  async function confirmSmsConsent() {
    setShowSmsConsent(false);
    setGlobalSms(true);
    const ok = await onUpdateChannels({ smsEnabled: true });
    if (!ok) setGlobalSms(false);
  }

  async function resetAll() {
    if (sms2faEnabled) {
      setShowSmsBlockMsg(true);
      return;
    }
    const prevEmail = globalEmail;
    const prevSms = globalSms;
    setGlobalEmail(true);
    setGlobalSms(false);
    setShowSmsBlockMsg(false);
    const ok = await onUpdateChannels({
      emailEnabled: true,
      smsEnabled: false,
    });
    if (!ok) {
      setGlobalEmail(prevEmail);
      setGlobalSms(prevSms);
    }
  }

  return (
    <div>
      <div className="pf-section-head">
        <h2>Notification Preferences</h2>
        <p>Control how you receive notifications. Changes apply immediately.</p>
      </div>

      <div className="pf-card">
        <div className="pf-card__title">
          Global Channels
          <Button
            type="button"
            className="pf-btn-secondary pf-btn-sm"
            onClick={resetAll}
          >
            Reset to Defaults
          </Button>
        </div>
        <div className="pf-toggle-row">
          <div className="pf-tr-info">
            <div className="pf-tr-info__label">Email Notifications</div>
            <div className="pf-tr-info__sub">
              Receive notifications to your registered email address
            </div>
          </div>
          <label className="pf-toggle">
            <Input
              type="checkbox"
              checked={globalEmail}
              disabled={savingNotifications}
              onChange={(e) => toggleGlobalEmail(e.target.checked)}
            />
            <span className="pf-toggle__track" />
          </label>
        </div>
        <div className="pf-toggle-row">
          <div className="pf-tr-info">
            <div className="pf-tr-info__label">SMS Notifications</div>
            <div className="pf-tr-info__sub">
              {phoneVerified && phone ? (
                <>
                  Receive text messages to {phone}&nbsp;&middot;&nbsp;
                  <span className="pf-pv-badge verified">&#10003; Verified</span>
                </>
              ) : (
                "Receive text messages to your verified phone number"
              )}
            </div>
            {showSmsBlockMsg && (
              <div className="pf-tr-block-msg">
                SMS Notifications cannot be disabled while SMS Two-Factor
                Authentication is enabled. Please disable SMS Two-Factor
                Authentication first.
              </div>
            )}
          </div>
          <label className="pf-toggle">
            <Input
              type="checkbox"
              checked={globalSms}
              disabled={savingNotifications}
              onChange={(e) => toggleGlobalSms(e.target.checked)}
            />
            <span className="pf-toggle__track" />
          </label>
        </div>
        <div className="pf-toggle-row">
          <div className="pf-tr-info">
            <div className="pf-tr-info__label">In-App Notifications</div>
            <div className="pf-tr-info__sub">
              Badge counts and notification bell inside the CSC platform. Always
              on.
            </div>
          </div>
          <label className="pf-toggle">
            <Input type="checkbox" checked disabled />
            <span className="pf-toggle__track" />
          </label>
        </div>
      </div>

      <div className="pf-notif-hint">
        In-app notifications cannot be disabled — they are required for platform
        functionality. SMS notifications require a verified phone number. Email
        is the default channel for all alerts. You can unsubscribe from
        individual types above without affecting other channels.
      </div>

      <CustomModal
        show={showSmsConsent}
        handleClose={() => setShowSmsConsent(false)}
        title="Enable SMS Notifications"
        footerContent={
          <>
            <Button
              type="button"
              className="pf-btn-secondary"
              onClick={() => setShowSmsConsent(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="pf-btn-primary"
              disabled={!smsConsentChecked || savingNotifications}
              onClick={confirmSmsConsent}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p>
          By enabling SMS notifications, you agree to receive text messages from
          CSC Construction regarding project updates, draw requests, and
          important alerts. Message and data rates may apply.
        </p>
        <div className="pf-consent-label">
          <Input
            type="checkbox"
            id="sms-consent-check"
            className="pf-consent-checkbox"
            checked={smsConsentChecked}
            onChange={(e) => setSmsConsentChecked(e.target.checked)}
          />
          <label htmlFor="sms-consent-check" className="pf-consent-text">
            I consent to receive SMS notifications and understand I can disable
            them at any time.
          </label>
        </div>
      </CustomModal>
    </div>
  );
}
